import { Box, Button, Typography, Paper, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { useConfirmation } from '../hooks/useConfirmation';
import { ConfirmationModal, AlertModal } from '../components/common/ConfirmationModal';
import { CustomSnackbar } from '../components/common/CustomSnackbar';

const ModalDemo = () => {
  const {
    confirmationState,
    alertState,
    snackbarState,
    showConfirmation,
    showAlert,
    closeConfirmation,
    closeAlert,
    handleConfirm,
    confirmDelete,
    confirmAction,
    showValidationAlert,
    showSuccessAlert,
    showErrorAlert,
    showInfoAlert,
    showSuccessSnackbar,
    showErrorSnackbar,
    showWarningSnackbar,
    showInfoSnackbar,
    closeSnackbar,
  } = useConfirmation();

  const handleDemoAction = () => {
    console.log('Demo action executed!');
  };

  return (
    <Box sx={{ p: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" gutterBottom>
          Custom Modal Demo
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          This page demonstrates the custom confirmation and alert modals that replace browser alerts.
        </Typography>

        <Grid container spacing={3}>
          {/* Confirmation Modals */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Confirmation Modals
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    showConfirmation(
                      {
                        title: 'Basic Confirmation',
                        message: 'Are you sure you want to proceed?',
                        variant: 'info',
                      },
                      handleDemoAction
                    )
                  }
                >
                  Basic Confirmation
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  onClick={() => confirmDelete('this demo item', handleDemoAction)}
                >
                  Delete Confirmation
                </Button>

                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => confirmAction('Publish', 'this content', handleDemoAction)}
                >
                  Action Confirmation
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    showConfirmation(
                      {
                        title: 'Custom Confirmation',
                        message: 'This is a custom confirmation with different styling.',
                        confirmText: 'Yes, Do It!',
                        cancelText: 'No, Cancel',
                        variant: 'warning',
                        confirmButtonColor: 'success',
                      },
                      handleDemoAction
                    )
                  }
                >
                  Custom Styled Confirmation
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Alert Modals */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Alert Modals
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => showInfoAlert('This is an informational message.')}
                >
                  Info Alert
                </Button>

                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => showSuccessAlert('Operation completed successfully!')}
                >
                  Success Alert
                </Button>

                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() =>
                    showAlert({
                      title: 'Warning',
                      message: 'This is a warning message.',
                      variant: 'warning',
                    })
                  }
                >
                  Warning Alert
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => showErrorAlert('An error occurred. Please try again.')}
                >
                  Error Alert
                </Button>

                <Button
                  variant="outlined"
                  onClick={() =>
                    showValidationAlert([
                      'Name is required',
                      'Email must be valid',
                      'Password must be at least 8 characters',
                    ])
                  }
                >
                  Validation Alert
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Snackbar Notifications */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Snackbar Notifications
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => showSuccessSnackbar('Operation completed successfully!')}
                >
                  Success Snackbar
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  onClick={() => showErrorSnackbar('An error occurred. Please try again.')}
                >
                  Error Snackbar
                </Button>

                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => showWarningSnackbar('This is a warning message.')}
                >
                  Warning Snackbar
                </Button>

                <Button
                  variant="contained"
                  color="info"
                  onClick={() => showInfoSnackbar('This is an informational message.')}
                >
                  Info Snackbar
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => showSuccessSnackbar('User profile updated successfully!', 'Profile Updated')}
                >
                  Snackbar with Title
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Demo Actions */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Demo Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    confirmAction('Save', 'this document', () => {
                      showSuccessSnackbar('Document saved successfully!');
                    });
                  }}
                >
                  Save Document (Confirm + Success)
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    confirmDelete('this user account', () => {
                      showSuccessSnackbar('Account deleted successfully!');
                    });
                  }}
                >
                  Delete Account (Confirm + Success)
                </Button>

                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => {
                    showErrorSnackbar('Failed to connect to server. Please check your connection.');
                  }}
                >
                  Simulate Network Error
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </motion.div>

      {/* Modal Components */}
      <ConfirmationModal
        open={confirmationState.open}
        onClose={closeConfirmation}
        onConfirm={handleConfirm}
        title={confirmationState.options.title}
        message={confirmationState.options.message}
        confirmText={confirmationState.options.confirmText}
        cancelText={confirmationState.options.cancelText}
        variant={confirmationState.options.variant}
        confirmButtonColor={confirmationState.options.confirmButtonColor}
      />

      <AlertModal
        open={alertState.open}
        onClose={closeAlert}
        title={alertState.options.title}
        message={alertState.options.message}
        confirmText={alertState.options.confirmText}
        variant={alertState.options.variant}
      />

      <CustomSnackbar
        snackbar={{
          open: snackbarState.open,
          message: snackbarState.message,
          severity: snackbarState.severity,
          title: snackbarState.title,
          autoHideDuration: snackbarState.autoHideDuration,
        }}
        onClose={closeSnackbar}
      />
    </Box>
  );
};

export default ModalDemo;
